import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';
import { useParams } from 'react-router-dom';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'tutorials';
  const category = categoryMap[currentSlug] || categories[0];
  const articles = getArticlesByCategory(currentSlug);

  const pageTitle = `${category.name} - ${siteInfo.name}`;
  const pageDesc = `${category.description} 浏览${articles.length}篇${category.name}相关的同人动画文章与教程。`;
  const pageUrl = `${siteInfo.url}/category/${currentSlug}`;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: pageUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
        { '@type': 'ListItem', position: 2, name: category.name },
      ],
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${category.name},同人动画,${siteInfo.keywords}`}
        canonicalUrl={pageUrl}
        ogType="website"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="pt-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="relative w-full h-48 md:h-64 rounded-lg overflow-hidden mb-8">
            <img
              src={category.image}
              alt={category.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent flex items-center">
              <div className="p-6 md:p-10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-500 rounded-lg">
                    <i className={`${category.icon} text-background-50 text-lg`}></i>
                  </div>
                  <h1 className="font-heading text-2xl md:text-4xl text-background-50">
                    {category.name}
                  </h1>
                </div>
                <p className="text-background-200 text-sm md:text-base max-w-lg">
                  {category.description}
                </p>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <CategoryTabs />
          </div>

          <div className="pb-14 md:pb-20">
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                  <i className="ri-inbox-line text-3xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}